import { useState, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, RefreshCw, Loader2, ImageOff, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { draftQueries } from '@/lib/api/queries/draft'
import { useSelectCandidate, useRegenerateCandidates } from '@/lib/api/mutations/draft'
import type { ImageCandidate } from '@/lib/api/types'

const PROMPT_TEMPLATE = 'floating 3D {{keyword}} icons , high-quality 3D render, soft matte finish with subtle translucency, colors: pastel periwinkle blue, soft candy pink, light peach orange, and pale buttercream yellow. soft studio lighting. Minimalist style, isolated on a solid white background, soft shadows'

function deriveKeyword(title: string): string {
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'how', 'why', 'what', 'when', 'is', 'are', 'was', 'were', 'do', 'does', 'not', 'from', 'into', 'after', 'before', 'most', 'your', 'their', 'its', 'that', 'this', 'can', 'will'])
  const words = title.toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w))
  return words.slice(0, 2).join(' ') || 'abstract'
}

function buildDefaultPrompt(title: string): string {
  return PROMPT_TEMPLATE.replace('{{keyword}}', deriveKeyword(title))
}

interface ImageCandidatesGridProps {
  agentId: string
  draftId: string
  draftTitle: string
  requireDraftStatus: (action: () => void) => void
  imageOpsPending: boolean
}

function CandidateThumbnail({
  candidate,
  onSelect,
  isPending,
}: {
  candidate: ImageCandidate
  onSelect: (id: string) => void
  isPending: boolean
}) {
  const [imgError, setImgError] = useState(false)

  return (
    <button
      type="button"
      className={`aspect-square rounded-md overflow-hidden cursor-pointer relative group ${
        candidate.selected ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-muted-foreground/30'
      }`}
      onClick={() => onSelect(candidate.id)}
      disabled={isPending}
    >
      {imgError ? (
        <div className="w-full h-full bg-muted flex flex-col items-center justify-center gap-1">
          <ImageOff className="size-5 text-muted-foreground/50" />
          <span className="text-[10px] text-muted-foreground">Expired</span>
        </div>
      ) : (
        <img
          src={candidate.imageUrl}
          alt={candidate.prompt.slice(0, 60)}
          className="object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      )}
      {candidate.selected && (
        <span className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5 size-5 flex items-center justify-center">
          <Check className="size-3" />
        </span>
      )}
    </button>
  )
}

export function ImageCandidatesGrid({
  agentId,
  draftId,
  draftTitle,
  requireDraftStatus,
  imageOpsPending,
}: ImageCandidatesGridProps) {
  const { data, isLoading } = useQuery(draftQueries.candidates(agentId, draftId))
  const selectMutation = useSelectCandidate(agentId, draftId)
  const regenerateMutation = useRegenerateCandidates(agentId, draftId)

  const [showRegenerate, setShowRegenerate] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [showPrompt, setShowPrompt] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const insertAtCursor = useCallback((text: string) => {
    const el = textareaRef.current
    if (!el) {
      setPrompt((prev) => (prev ? prev + ' ' + text : text))
      return
    }
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = prompt.slice(0, start)
    const after = prompt.slice(end)
    const needsSpace = before.length > 0 && !before.endsWith(' ')
    const inserted = (needsSpace ? ' ' : '') + text
    const newPrompt = before + inserted + after
    setPrompt(newPrompt)
    requestAnimationFrame(() => {
      const cursorPos = start + inserted.length
      el.focus()
      el.setSelectionRange(cursorPos, cursorPos)
    })
  }, [prompt])

  const suggestionsQuery = useQuery({
    ...draftQueries.promptSuggestions(agentId, draftId),
    enabled: showRegenerate,
  })

  const candidates = data?.data ?? []
  const currentPrompt = candidates[0]?.prompt ?? null

  const handleOpenRegenerate = () => {
    const apiPrompt = suggestionsQuery.data?.currentPrompt
    setPrompt(apiPrompt ?? currentPrompt ?? buildDefaultPrompt(draftTitle))
    setShowRegenerate(true)
  }

  const suggestions = suggestionsQuery.data?.suggestions ?? []

  const handleGenerate = () => {
    if (!prompt.trim()) return
    requireDraftStatus(() =>
      regenerateMutation.mutate(
        { prompt: prompt.trim() },
        {
          onSuccess: () => {
            setShowRegenerate(false)
          },
        },
      ),
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">Image Candidates</p>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-md" />
          ))}
        </div>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No image candidates generated yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {candidates.map((candidate) => (
            <CandidateThumbnail
              key={candidate.id}
              candidate={candidate}
              onSelect={(id) => requireDraftStatus(() => selectMutation.mutate(id))}
              isPending={selectMutation.isPending || imageOpsPending}
            />
          ))}
        </div>
      )}

      {/* Current prompt display */}
      {currentPrompt && (
        <button
          type="button"
          className="flex items-start gap-1.5 text-left w-full"
          onClick={() => setShowPrompt(!showPrompt)}
        >
          {showPrompt ? (
            <ChevronUp className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          )}
          <span className={`text-xs text-muted-foreground ${showPrompt ? '' : 'line-clamp-1'}`}>
            {currentPrompt}
          </span>
        </button>
      )}

      {/* Regenerate section */}
      <div className="pt-1">
        {!showRegenerate ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenRegenerate}
            disabled={imageOpsPending}
            className="w-full"
          >
            {imageOpsPending && <Loader2 className="mr-2 size-3.5 animate-spin" />}
            <RefreshCw className="mr-2 size-3.5" />
            Regenerate Images
          </Button>
        ) : (
          <div className="space-y-2">
            <Textarea
              ref={textareaRef}
              rows={4}
              placeholder="Describe the image you want..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={2000}
              className="text-xs"
            />
            {/* Prompt suggestions chips */}
            {suggestionsQuery.isLoading && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                <span>Loading suggestions...</span>
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-muted hover:bg-muted/80 cursor-pointer transition-colors"
                    onClick={() => insertAtCursor(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={regenerateMutation.isPending || imageOpsPending || !prompt.trim()}
              >
                {regenerateMutation.isPending && (
                  <Loader2 className="mr-2 size-3.5 animate-spin" />
                )}
                Generate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowRegenerate(false)
                  setPrompt('')
                }}
                disabled={regenerateMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
